import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { obtenerResumenDashboardTienda } from "../../../services/usuarios_tienda/dashboard";
import { obtenerConfiguracionTiendaService } from "../../../services/usuarios_tienda/ConfiguracionTienda";
import { formatearPrecio } from "../../../utils/moneda";
import "./TiendaDashboardPage.css";

const ESTADO_ITEMS = [
  { key: "PENDIENTE", label: "Pendiente", tone: "warning" },
  { key: "EN_PROCESO", label: "En proceso", tone: "info" },
  { key: "EN_RUTA", label: "En ruta", tone: "primary" },
  { key: "ENTREGADO", label: "Entregado", tone: "success" },
  { key: "CANCELADO", label: "Cancelado", tone: "danger" },
];

function TiendaDashboardPage() {
  const [data, setData] = useState(null);
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const [dashRes, cfgRes] = await Promise.all([
        obtenerResumenDashboardTienda(),
        obtenerConfiguracionTiendaService(),
      ]);

      if (!dashRes?.ok) {
        setError(dashRes?.message || "No se pudo cargar el resumen.");
        setData(null);
      } else {
        setData(dashRes.data || null);
      }

      if (cfgRes?.ok && cfgRes.data) {
        setConfig(cfgRes.data);
      }
    } catch (e) {
      setError(e?.message || "Error al cargar el dashboard.");
      setData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const fmt = (n) =>
    formatearPrecio(n, config, { espacioEntreSimbolo: true }) || "—";

  const porEstado = useMemo(
    () => data?.pedidos_por_estado || {},
    [data?.pedidos_por_estado]
  );

  const totalEstados = useMemo(() => {
    return ESTADO_ITEMS.reduce((acc, item) => acc + Number(porEstado[item.key] ?? 0), 0);
  }, [porEstado]);

  if (loading) {
    return (
      <div className="tdash tdash--state">
        <div className="tdash-state-card">
          <div className="tdash-state-card__title">Cargando dashboard...</div>
          <p className="tdash-state-card__text">
            Estamos preparando el resumen de tu tienda.
          </p>
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="tdash tdash--state">
        <div className="tdash-state-card tdash-state-card--error">
          <div className="tdash-state-card__title">No se pudo cargar el dashboard</div>
          <p className="tdash-state-card__text">{error || "Sin datos disponibles."}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="tdash">
      <div className="tdash__inner">
        <div className="tdash__topbar">
          <div>
            <h1 className="tdash__title">Dashboard</h1>
            <p className="tdash__subtitle">
              Vista general de ventas, pagos y estados de envío de tu tienda.
            </p>
          </div>

          <Link className="tdash__action" to="/administracion/tienda/orders">
            Ver pedidos
          </Link>
        </div>

        <section className="tdash-hero">
          <div className="tdash-hero__content">
            <span className="tdash-hero__eyebrow">Ventas pagadas</span>
            <div className="tdash-hero__amount">{fmt(data.ventas_total_pagado)}</div>
            <p className="tdash-hero__text">
              {data.cantidad_pagados} pedido{data.cantidad_pagados === 1 ? "" : "s"} con pago
              confirmado.
            </p>
          </div>
        </section>

        <section className="tdash-grid tdash-grid--summary">
          <article className="tdash-card">
            <div className="tdash-card__label">Pedidos pagados</div>
            <div className="tdash-card__value">{data.cantidad_pagados}</div>
            <div className="tdash-card__meta">Con pago confirmado</div>
          </article>

          <article className="tdash-card">
            <div className="tdash-card__label">Pendientes de pago</div>
            <div className="tdash-card__value">{data.cantidad_pago_pendiente}</div>
            <div className="tdash-card__meta">
              Monto aproximado: {fmt(data.monto_pendiente_cobro)}
            </div>
          </article>

          <article className="tdash-card">
            <div className="tdash-card__label">Total de pedidos en estados</div>
            <div className="tdash-card__value">{totalEstados}</div>
            <div className="tdash-card__meta">Suma de estados de envío registrados</div>
          </article>
        </section>

        <section className="tdash-panel">
          <div className="tdash-panel__head">
            <div>
              <h2 className="tdash-panel__title">Estados de envío</h2>
              <p className="tdash-panel__sub">
                Distribución actual de pedidos por estado.
              </p>
            </div>
          </div>

          <div className="tdash-status-list">
            {ESTADO_ITEMS.map(({ key, label, tone }) => {
              const cantidad = Number(porEstado[key] ?? 0);
              const percentage =
                totalEstados > 0 ? Math.round((cantidad / totalEstados) * 100) : 0;

              return (
                <div key={key} className="tdash-status-item">
                  <div className="tdash-status-item__top">
                    <div className="tdash-status-item__left">
                      <span className={`tdash-status-item__dot tdash-status-item__dot--${tone}`} />
                      <span className="tdash-status-item__label">{label}</span>
                    </div>

                    <div className="tdash-status-item__right">
                      <span className="tdash-status-item__value">{cantidad}</span>
                      <span className="tdash-status-item__percent">{percentage}%</span>
                    </div>
                  </div>

                  <div className="tdash-status-item__bar">
                    <div
                      className={`tdash-status-item__fill tdash-status-item__fill--${tone}`}
                      style={{ width: `${percentage}%` }}
                    />
                  </div>
                </div>
              );
            })}
          </div>

          {Number(porEstado.OTRO) > 0 && (
            <div className="tdash-note">
              Otros / sin clasificar: <strong>{porEstado.OTRO}</strong>
            </div>
          )}
        </section>
      </div>
    </div>
  );
}

export default TiendaDashboardPage;